import React, { useState, useEffect, useCallback } from "react";
import { getNetWorthSummary, getNetWorthHistory, getManualAssets, getLiabilities } from "../lib/api";
import { Area, AreaChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Lock, IndianRupee, Briefcase, Landmark, Gem, Building2, PiggyBank, Banknote, CarFront, Loader2, AlertTriangle, RefreshCw, Bitcoin, CreditCard, TrendingUp, FileCheck2 } from "lucide-react";
import { Button } from "../components/ui/button";

const TYPE_META = {
  debt: { label: "Debt & Retirement", icon: Landmark, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/20" },
  alternative: { label: "Gold & Alternatives", icon: Gem, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
  real_estate: { label: "Real Estate", icon: Building2, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
  bank_savings: { label: "Bank Savings", icon: PiggyBank, color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  fd: { label: "Fixed Deposits", icon: Banknote, color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20" },
  vehicle: { label: "Vehicles", icon: CarFront, color: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/20" },
};

function fmtINR(v) {
  if (!v && v !== 0) return "--";
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function NetWorthPage() {
  return <NetWorthOverview />;
}

function NetWorthOverview() {
  const [nw, setNw] = useState(null);
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [history, setHistory] = useState(null);
  const [historyYear, setHistoryYear] = useState(2026);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    const [n, a, l, h] = await Promise.allSettled([
      getNetWorthSummary(),
      getManualAssets(),
      getLiabilities(),
      getNetWorthHistory(),
    ]);
    let gotAny = false;
    if (n.status === "fulfilled") { setNw(n.value.data); gotAny = true; }
    if (a.status === "fulfilled") { setAssets(a.value.data?.assets || []); gotAny = true; }
    if (l.status === "fulfilled") { setLiabilities(l.value.data?.liabilities || []); gotAny = true; }
    if (h.status === "fulfilled") {
      setHistory(h.value.data);
      const latest = h.value.data?.summary?.latest_year;
      if (latest) setHistoryYear(latest);
      gotAny = true;
    }
    if (!gotAny) setError("Could not load Net Worth data. Please retry.");
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-retry up to 3 times with exponential backoff
  useEffect(() => {
    if (!error || retryCount >= 3) return;
    const delay = 2000 * Math.pow(2, retryCount); // 2s, 4s, 8s
    const t = setTimeout(() => { setRetryCount(c => c + 1); load(); }, delay);
    return () => clearTimeout(t);
  }, [error, retryCount, load]);

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="animate-spin text-[var(--brand)]" size={28} /></div>;
  }

  // Hard error state — no data came through
  if (error && !nw) {
    return (
      <div className="flex items-center justify-center h-[60vh] p-6" data-testid="networth-error">
        <div className="bg-[var(--bg-surface)] border border-amber-500/30 rounded-lg p-6 max-w-sm text-center">
          <AlertTriangle size={28} className="text-amber-400 mx-auto mb-3" />
          <p className="text-sm text-amber-300 font-medium mb-1">Net Worth data unavailable</p>
          <p className="text-xs text-[var(--text-muted)] mb-4">{retryCount < 3 ? `Retrying… (${retryCount + 1}/3)` : "Could not load after retries."}</p>
          <Button onClick={() => { setRetryCount(0); setLoading(true); load(); }} variant="outline" size="sm" className="gap-1.5 border-amber-500/30" data-testid="networth-retry">
            <RefreshCw size={13} /> Retry now
          </Button>
        </div>
      </div>
    );
  }

  const equity = nw?.portfolio_value || 0;
  const equityPnl = nw?.portfolio_pnl || 0;
  const equityPnlPct = nw?.portfolio_pnl_pct || 0;
  const manualTotal = nw?.manual_assets_value || 0;
  const cryptoTotal = nw?.crypto_value || 0;
  const liabilitiesTotal = nw?.total_liabilities || 0;
  const grossAssets = nw?.gross_assets || (equity + manualTotal + cryptoTotal);
  const grand = nw?.net_worth ?? nw?.grand_total ?? grossAssets;
  const investible = nw?.investible_assets || 0;
  const allZero = grossAssets === 0 && equity === 0 && manualTotal === 0 && cryptoTotal === 0;
  const historyRows = history?.snapshots || [];
  const selectedHistory = historyRows.find(row => row.year === Number(historyYear)) || historyRows[historyRows.length - 1];
  const historyChartRows = (() => {
    if (!historyRows.length) return [];
    const rows = historyRows.map(row => ({ ...row, historical_value: row.reported_net_worth, live_path: null }));
    const latestRow = rows[rows.length - 1];
    const liveValue = latestRow?.reconciliation?.system_net_worth;
    if (liveValue == null) return rows;
    latestRow.live_path = latestRow.reported_net_worth;
    return [
      ...rows,
      {
        year: "Live today",
        historical_value: null,
        live_path: liveValue,
        reported_net_worth: liveValue,
        is_live: true,
        change_amount: liveValue - latestRow.reported_net_worth,
        change_pct: latestRow.reported_net_worth
          ? ((liveValue / latestRow.reported_net_worth) - 1) * 100
          : null,
      },
    ];
  })();

  // Group manual assets by asset_type
  const grouped = {};
  for (const a of assets) {
    const t = a.asset_type || "other";
    if (!grouped[t]) grouped[t] = { total: 0, count: 0, items: [] };
    grouped[t].total += (a.current_value_inr ?? a.current_value ?? 0);
    grouped[t].count += 1;
    grouped[t].items.push(a);
  }
  const orderedTypes = ["real_estate", "fd", "debt", "bank_savings", "alternative"];
  const sortedGroups = orderedTypes.filter(t => grouped[t]).map(t => [t, grouped[t]]);

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6" data-testid="networth-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Lock size={14} className="text-[var(--brand)]" />
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Net Worth Overview</h1>
          </div>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">Gross assets − liabilities, normalized to INR</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setRetryCount(0); load(); }} variant="outline" size="sm" className="gap-1.5 border-white/10 text-xs" data-testid="networth-refresh-btn">
            <RefreshCw size={13} /> Refresh
          </Button>
          <div className="flex items-center gap-1 text-[10px] text-emerald-400 px-2">
            <Lock size={11} /> Protected by your signed-in session
          </div>
        </div>
      </div>

      {/* Stale-data warning when everything is zero (broker sync incomplete) */}
      {allZero && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-md p-3 flex items-center gap-3" data-testid="networth-stale-banner">
          <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />
          <div className="flex-1 text-xs">
            <span className="text-amber-300 font-medium">Data may still be loading.</span>
            <span className="text-[var(--text-muted)] ml-1">If your broker sync recently happened, prices need a few seconds to fetch. Try refreshing.</span>
          </div>
          <Button onClick={() => { setRetryCount(0); load(); }} variant="outline" size="sm" className="text-xs border-amber-500/30 gap-1">
            <RefreshCw size={12} /> Retry
          </Button>
        </div>
      )}

      {(nw?.data_quality?.warnings || []).length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-md p-3 flex items-start gap-3" data-testid="networth-quality-banner">
          <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs">
            <div className="text-amber-300 font-medium">Review data quality</div>
            {(nw.data_quality.warnings || []).map(w => <div key={w} className="text-[var(--text-muted)] mt-0.5">{w}</div>)}
          </div>
        </div>
      )}

      {/* Hero — Grand Net Worth */}
      <div data-tone="green" className="semantic-card border rounded-lg p-6" data-testid="networth-hero">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand)] font-semibold flex items-center gap-1.5">
              <IndianRupee size={11} /> Household Net Worth
            </div>
            <div className="semantic-value font-mono text-4xl md:text-5xl font-bold mt-2" data-testid="networth-grand-value">{fmtINR(grand)}</div>
            <div className="text-xs text-[var(--text-muted)] mt-2">
              Gross assets {fmtINR(grossAssets)} − liabilities {fmtINR(liabilitiesTotal)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Equity P&L</div>
            <div className={`font-mono text-lg ${equityPnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
              {equityPnl >= 0 ? "+" : ""}{fmtINR(equityPnl)}
            </div>
            <div className={`font-mono text-[11px] ${equityPnlPct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
              {equityPnlPct >= 0 ? "+" : ""}{equityPnlPct}%
            </div>
          </div>
        </div>

        {/* Allocation bar */}
        {grossAssets > 0 && (
          <div className="mt-5">
            <div className="flex h-2 rounded-full overflow-hidden bg-white/5">
              <div className="bg-[var(--brand)]" style={{ width: `${(equity / grossAssets) * 100}%` }} title={`Equity ${fmtINR(equity)}`} />
              {cryptoTotal > 0 && <div className="bg-amber-400" style={{ width: `${(cryptoTotal / grossAssets) * 100}%` }} title={`Crypto ${fmtINR(cryptoTotal)}`} />}
              {sortedGroups.map(([t, g]) => {
                const meta = TYPE_META[t];
                const pct = (g.total / grossAssets) * 100;
                return <div key={t} className={meta?.bg.replace("/10", "/40")} style={{ width: `${pct}%`, background: meta ? `var(--${t === "real_estate" ? "positive" : "warning"})` : undefined }} title={`${meta?.label} ${fmtINR(g.total)}`} />;
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-[var(--text-muted)]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[var(--brand)]" /> Equity {((equity / grossAssets) * 100).toFixed(1)}%</span>
              {cryptoTotal > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400" /> Crypto {((cryptoTotal / grossAssets) * 100).toFixed(1)}%</span>}
              {sortedGroups.map(([t, g]) => {
                const meta = TYPE_META[t];
                return <span key={t} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-sm ${meta?.bg}`} /> {meta?.label} {((g.total / grossAssets) * 100).toFixed(1)}%</span>;
              })}
            </div>
          </div>
        )}
      </div>

      {historyRows.length > 0 && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg overflow-hidden" data-testid="networth-history">
          <div className="p-5 border-b border-white/10 flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2">
                <TrendingUp size={16} className="text-[var(--brand)]" />
                <h2 className="text-lg font-semibold text-white">Historical progress</h2>
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Annual values reported in Personal_Finance.pdf · source preserved
              </p>
            </div>
            <div className="flex gap-5">
              <HistoryStat label="Growth" value={fmtINR(history.summary?.growth_amount)} />
              <HistoryStat label="Multiple" value={`${history.summary?.growth_multiple || "—"}×`} />
              <HistoryStat label="CAGR" value={history.summary?.cagr_pct != null ? `${history.summary.cagr_pct}%` : "—"} />
            </div>
          </div>

          <div className="p-4 md:p-5">
            <div className="h-[310px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={historyChartRows} margin={{ top: 15, right: 20, left: 5, bottom: 0 }}>
                  <defs>
                    <linearGradient id="netWorthHistoryFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--brand)" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="var(--brand)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,.08)" vertical={false} />
                  <XAxis dataKey="year" tick={{ fill: "#8291aa", fontSize: 11 }} axisLine={{ stroke: "#42506a" }} tickLine={false} />
                  <YAxis tickFormatter={value => `₹${(value / 1e7).toFixed(0)}Cr`} tick={{ fill: "#8291aa", fontSize: 11 }} axisLine={false} tickLine={false} width={54} />
                  <Tooltip content={<NetWorthTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="historical_value"
                    stroke="var(--brand)"
                    strokeWidth={3}
                    fill="url(#netWorthHistoryFill)"
                    dot={{ r: 4, fill: "var(--brand)", stroke: "#091116", strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="linear"
                    dataKey="live_path"
                    stroke="#60a5fa"
                    strokeWidth={2.5}
                    strokeDasharray="7 7"
                    connectNulls={false}
                    dot={{ r: 4, fill: "#60a5fa", stroke: "#091116", strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {historyChartRows.some(row => row.is_live) && (
              <div className="flex justify-end items-center gap-2 text-[10px] text-[var(--text-muted)] -mt-1 mr-4">
                <span className="w-7 border-t-2 border-dashed border-blue-400" />
                Live Capital360 continuation—not a 2027 forecast
              </div>
            )}

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4 border-t border-white/10 pt-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Inspect year</label>
                <select
                  value={historyYear}
                  onChange={event => setHistoryYear(Number(event.target.value))}
                  className="mt-2 w-full bg-[var(--bg-base)] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                >
                  {historyRows.map(row => <option key={row.year} value={row.year}>{row.year}</option>)}
                </select>
                {selectedHistory && (
                  <div className="mt-3">
                    <div className="font-mono text-2xl font-bold text-white">{fmtINR(selectedHistory.reported_net_worth)}</div>
                    <div className={`text-xs mt-1 ${(selectedHistory.change_pct || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {selectedHistory.change_pct == null ? "Starting point" : `${selectedHistory.change_pct >= 0 ? "+" : ""}${selectedHistory.change_pct}% year on year`}
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-[var(--text-muted)] mt-3">
                      <FileCheck2 size={11} /> PDF page 1
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                {Object.entries(selectedHistory?.breakdown || {})
                  .sort(([, a], [, b]) => b - a)
                  .map(([label, value]) => (
                    <div key={label} className="bg-[var(--bg-base)] border border-white/5 rounded-md p-3">
                      <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)] truncate" title={label}>{label}</div>
                      <div className="font-mono text-sm text-white mt-1">{fmtINR(value)}</div>
                    </div>
                  ))}
              </div>
            </div>

            {selectedHistory?.reconciliation?.status === "reconciled_current" && (
              <div className="mt-4 bg-cyan-500/5 border border-cyan-500/20 rounded-md p-3 text-xs">
                <span className="text-cyan-300 font-medium">Current reconciliation:</span>
                <span className="text-[var(--text-muted)] ml-1">
                  Capital360 calculates {fmtINR(selectedHistory.reconciliation.system_net_worth)} today,
                  a difference of {fmtINR(selectedHistory.reconciliation.variance)} from the PDF snapshot.
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reconciled balance-sheet summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="networth-equity-card">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase size={14} className="text-[var(--brand)]" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] font-semibold">Equity Portfolio</span>
          </div>
          <div className="font-mono text-2xl font-bold text-white">{fmtINR(equity)}</div>
          <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
            {nw?.portfolio_counts?.total || 0} holdings · India + MF + US
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <MiniStat label="India" value={fmtINR(nw?.portfolio_breakdown?.india || 0)} />
            <MiniStat label="MF" value={fmtINR(nw?.portfolio_breakdown?.mutual_fund || 0)} />
            <MiniStat label="US" value={fmtINR(nw?.portfolio_breakdown?.us || 0)} />
          </div>
        </div>

        <div className="bg-[var(--bg-surface)] border border-amber-500/20 rounded-lg p-5" data-testid="networth-crypto-card">
          <div className="flex items-center gap-2 mb-3">
            <Bitcoin size={14} className="text-amber-400" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] font-semibold">Crypto Portfolio</span>
          </div>
          <div className="font-mono text-2xl font-bold text-white">{fmtINR(cryptoTotal)}</div>
          <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
            {cryptoTotal > 0 ? "Live INR via CoinDCX/CoinGecko" : "Add coins in Crypto Portfolio"}
          </div>
          {grossAssets > 0 && (
            <div className="text-[10px] text-[var(--text-muted)] mt-3">
              {((cryptoTotal / grossAssets) * 100).toFixed(1)}% of gross assets
            </div>
          )}
        </div>

        <div className="bg-[var(--bg-surface)] border border-red-500/20 rounded-lg p-5" data-testid="networth-liabilities-card">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard size={14} className="text-red-400" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] font-semibold">Liabilities</span>
          </div>
          <div className="font-mono text-2xl font-bold text-white">{fmtINR(liabilitiesTotal)}</div>
          <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
            {liabilities.length} recorded obligation{liabilities.length === 1 ? "" : "s"}
          </div>
          <div className="text-[10px] text-[var(--text-muted)] mt-3">Investible assets: {fmtINR(investible)}</div>
        </div>

        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-5" data-testid="networth-other-card">
          <div className="flex items-center gap-2 mb-3">
            <Landmark size={14} className="text-[var(--brand)]" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] font-semibold">Other Assets</span>
          </div>
          <div className="font-mono text-2xl font-bold text-white">{fmtINR(manualTotal)}</div>
          <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
            {assets.length} entries · across {sortedGroups.length} categories
          </div>
          {sortedGroups.length === 0 && (
            <p className="text-xs text-[var(--text-muted)] mt-3">Add entries in <span className="text-[var(--brand)]">Wealth Holdings</span></p>
          )}
        </div>
      </div>

      {/* Category breakdown grid */}
      {sortedGroups.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--text-muted)] font-semibold mb-3">By Category</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {sortedGroups.map(([t, g]) => {
              const meta = TYPE_META[t] || { label: t, icon: Landmark, color: "text-white", bg: "bg-white/5", border: "border-white/10" };
              const Icon = meta.icon;
              return (
                <div key={t} className={`${meta.bg} ${meta.border} border rounded-lg p-4`} data-testid={`networth-cat-${t}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon size={14} className={meta.color} />
                      <span className="text-xs font-semibold text-white">{meta.label}</span>
                    </div>
                    <span className="text-[9px] text-[var(--text-muted)]">{g.count} item{g.count !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="font-mono text-xl font-bold text-white">{fmtINR(g.total)}</div>
                  <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                    {grand > 0 ? `${((g.total / grand) * 100).toFixed(1)}% of net worth` : "—"}
                  </div>
                  <div className="mt-3 space-y-1 max-h-[120px] overflow-y-auto pr-1">
                    {g.items.slice(0, 6).map(it => (
                      <div key={it.id} className="flex items-center justify-between text-[11px]">
                        <span className="text-[var(--text-secondary)] truncate flex-1 mr-2">
                          {it.name || it.category}
                          {it.holder && <span className="text-[var(--text-muted)] ml-1">· {it.holder}</span>}
                        </span>
                        <span className="font-mono text-white whitespace-nowrap">{fmtINR(it.current_value_inr ?? it.current_value ?? 0)}</span>
                      </div>
                    ))}
                    {g.items.length > 6 && (
                      <div className="text-[10px] text-[var(--text-muted)] italic pt-1">+ {g.items.length - 6} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-[10px] text-[var(--text-muted)] text-center pt-2">
        Manage individual assets in <span className="text-[var(--brand)]">Wealth Holdings</span>. Equity sync happens on the Equity Dashboard.
      </p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-[var(--bg-base)] border border-white/5 rounded p-2">
      <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">{label}</div>
      <div className="font-mono text-sm text-white truncate">{value}</div>
    </div>
  );
}

function HistoryStat({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">{label}</div>
      <div className="font-mono text-sm font-semibold text-white">{value}</div>
    </div>
  );
}

function NetWorthTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-[#0d1218] border border-white/15 rounded-md p-3 shadow-xl">
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{row.is_live ? "Live today" : row.year}</div>
      <div className="font-mono text-base font-semibold text-white mt-1">{fmtINR(row.reported_net_worth)}</div>
      {row.change_pct != null && (
        <div className={`text-[11px] mt-1 ${row.change_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
          {row.change_pct >= 0 ? "+" : ""}{Number(row.change_pct).toFixed(2)}% {row.is_live ? "since 2026 snapshot" : "YoY"}
        </div>
      )}
    </div>
  );
}
